/**
 * Dwie skale porównawcze pod krzywą uziarnienia — przeniesione z prototypu GranuFilt.
 *
 * Skale są celowo NIEJEDNORODNE i tak było w prototypie:
 *  · szczelina — liniowa 0–5 mm, bo znormalizowane szczeliny PVC-U kończą się na 3 mm
 *    i liniowa skala pokazuje różnicę 0,5 vs 0,75 mm czytelniej niż logarytmiczna;
 *  · uziarnienie obsypki — logarytmiczna 0,25–40 mm, bo frakcje handlowe sięgają 40 mm
 *    i skala liniowa zgniotłaby cały zakres piaskowy do lewej krawędzi.
 * Wartości poza zakresem są klamrowane do krańca osi (jak w źródle).
 */
import type { SieveResult } from '@/types';

const numS = (d: number) => String(d).replace('.', ',');

const SLOT_MAX = 5;
const PACK_LO = 0.25;
const PACK_HI = 40;

interface ScaleProps {
  result: SieveResult;
  /** Szczelina wybrana przez geologa (tekst z listy, np. „1,5") — rysowana jako znacznik decyzji. */
  decisionSlot?: string | null;
  className?: string;
}

/** Procentowa pozycja na osi liniowej 0–5 mm. */
const slotPct = (v: number) => (Math.min(Math.max(v, 0), SLOT_MAX) / SLOT_MAX) * 100;

/** Procentowa pozycja na osi logarytmicznej 0,25–40 mm. */
const packPct = (v: number) => {
  const c = Math.min(Math.max(v, PACK_LO), PACK_HI);
  return ((Math.log10(c) - Math.log10(PACK_LO)) / (Math.log10(PACK_HI) - Math.log10(PACK_LO))) * 100;
};

/**
 * Liczba z polskiego zapisu. Zamiana GLOBALNA — bez flagi „g" tekst z dwoma przecinkami
 * („1,0 – 1,5") dawał parseFloat = 1.0, czyli marker cicho lądował na dolnym krańcu zakresu.
 * Decyzja powinna być pojedynczą wartością ze słownika, ale nie chcemy tu milczącego błędu.
 */
const parseDecimal = (s?: string | null): number | null => {
  if (!s) return null;
  const t = String(s).replace(/,/g, '.').trim();
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const v = parseFloat(t);
  return Number.isNaN(v) ? null : v;
};

/** Porównanie szczeliny filtra [mm] — marker PN, pasmo DVGW, marker decyzji geologa. */
export function SlotScale({ result, decisionSlot, className }: ScaleProps) {
  const pn = result.pn;
  const dv = result.dvgw;
  // PN: przy filtrze z obsypką liczy się szczelina z 0,8·Dmin, bez obsypki — z przedziału d50/3…d50.
  const pnSlot = pn.type === 'pack' ? pn.slot : pn.slotNo;
  const dvShow = dv.slotMin != null && !dv.natural;
  // Backend ustawia slotMax razem ze slotMin (dla jednowartościowej etykiety oba są równe),
  // ale typ dopuszcza null — bez tego fallbacku szerokość pasma wyszłaby NaN.
  const dvLo = dv.slotMin ?? 0;
  const dvHi = dv.slotMax ?? dvLo;
  const dec = parseDecimal(decisionSlot);

  return (
    <div className={className}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-ink-400 mb-1.5">
        Porównanie szczeliny filtra [mm]
      </div>
      <div className="relative h-[34px] rounded-md bg-ink-50 border border-ink-100">
        {dvShow && (
          <div className="absolute top-0 bottom-0 bg-green-500/20 border-x border-green-500/50"
               style={{
                 left: slotPct(dvLo) + '%',
                 width: Math.max(1.2, slotPct(dvHi) - slotPct(dvLo)) + '%',
               }}
               title={`DVGW W 113: ${dv.slotTxt ?? '–'} mm`} />
        )}
        {pnSlot != null && (
          <div className="absolute top-0 bottom-0 w-[2px] bg-water-500" style={{ left: slotPct(pnSlot) + '%' }}
               title={`PN-G-02323: ${numS(pnSlot)} mm`} />
        )}
        {dec != null && (
          <div className="absolute -top-[5px] -ml-[5px] w-0 h-0"
               style={{
                 left: slotPct(dec) + '%',
                 borderLeft: '5px solid transparent',
                 borderRight: '5px solid transparent',
                 borderTop: '7px solid #1b2a32',
               }}
               title={`Decyzja geologa: ${decisionSlot} mm`} />
        )}
        {[0, 1, 2, 3, 4, 5].map(v => (
          <div key={v} className="absolute bottom-0 text-[9.5px] text-ink-400 -translate-x-1/2"
               style={{ left: slotPct(v) + '%' }}>{v}</div>
        ))}
      </div>
      <div className="flex gap-3 flex-wrap mt-1 text-[11px] font-semibold">
        <span className="text-water-600">▏PN {pnSlot != null ? numS(pnSlot) : '–'}</span>
        <span className="text-green-600">▮ DVGW {dv.natural ? '–' : (dv.slotTxt ?? '–')}</span>
        <span className="text-ink-700">▲ Decyzja {decisionSlot ?? '–'}</span>
        <span className="text-ink-400 font-normal">szczelina [mm]</span>
      </div>
    </div>
  );
}

/** Porównanie uziarnienia obsypki [mm] — pasmo PN-88/B-06715 i grupa DIN 4924. */
export function PackScale({ result, className }: ScaleProps) {
  const pn = result.pn;
  const dv = result.dvgw;
  const pnShow = pn.type === 'pack' && pn.fracLo != null && pn.fracHi != null;
  const dvShow = !dv.natural && dv.groupLo != null && dv.groupHi != null;

  return (
    <div className={className}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-ink-400 mb-1.5">
        Porównanie uziarnienia obsypki [mm]
      </div>
      <div className="relative h-[34px] rounded-md bg-ink-50 border border-ink-100">
        {pnShow && (
          <div className="absolute top-[3px] h-[11px] rounded-sm bg-water-500/30 border border-water-500/60"
               style={{
                 left: packPct(pn.fracLo as number) + '%',
                 width: Math.max(1.2, packPct(pn.fracHi as number) - packPct(pn.fracLo as number)) + '%',
               }}
               title={`PN-88/B-06715: ${pn.fracLabel ?? ''} mm`} />
        )}
        {dvShow && (
          <div className="absolute top-[16px] h-[11px] rounded-sm bg-green-500/25 border border-green-500/60"
               style={{
                 left: packPct(dv.groupLo as number) + '%',
                 width: Math.max(1.2, packPct(dv.groupHi as number) - packPct(dv.groupLo as number)) + '%',
               }}
               title={`DIN 4924: ${dv.groupLabel ?? ''} mm`} />
        )}
        {[0.25, 0.5, 1, 2, 4, 8, 16, 32].map(v => (
          <div key={v} className="absolute bottom-0 text-[9.5px] text-ink-400 -translate-x-1/2"
               style={{ left: packPct(v) + '%' }}>{numS(v)}</div>
        ))}
      </div>
      <div className="flex gap-3 flex-wrap mt-1 text-[11px] font-semibold">
        <span className="text-water-600">▮ PN {pnShow ? pn.fracLabel : '—'}</span>
        <span className="text-green-600">▮ DVGW {dvShow ? dv.groupLabel : '—'}</span>
        <span className="text-ink-400 font-normal">uziarnienie [mm] · skala logarytmiczna</span>
      </div>
    </div>
  );
}
